import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { FaSave, FaUndo, FaPlus, FaTrash, FaArrowUp, FaArrowDown } from 'react-icons/fa';
import { FaBookOpen } from 'react-icons/fa';
import { useToast } from '../../../context/ToastContext';
import PageLayout from '../../../components/ui/PageLayout';
import PageHeader from '../../../components/ui/PageHeader';
import SkeletonLoader from '../../../components/ui/SkeletonLoader';

const SLUG = 'bookkeeping';

const emptyCategory = () => ({
  key: '',
  title: '',
  description: '',
  isActive: true,
});

const defaultForm = () => ({
  isPublished: true,
  pageTitle: 'Bookkeeping',
  pageSubtitle: '',
  loadingMessage: '',
  bundleBannerText: '',
  scheduleBannerTitle: '',
  scheduleFrequencyPrefix: '',
  scheduleStartedPrefix: '',
  scheduleDueDateLabel: '',
  financialRecordsTitle: '',
  financialRecordsEmpty: '',
  errorRetryLabel: '',
  errorRefreshingLabel: '',
  stepCards: [
    { step: 1, title: '', instructions: '', statusMessage: '' },
    { step: 2, title: '', instructions: '', statusMessage: '' },
    { step: 3, title: '', instructions: '', statusMessage: '' },
    { step: 4, title: '', instructions: '', statusMessage: '' },
  ],
  checklistCategories: [emptyCategory()],
  step1Modal: {},
  queriesModal: { columns: {} },
  step3Modal: {},
  uploadModal: {},
  toasts: {},
});

const TABS = [
  { id: 'page', label: 'Page & Banners' },
  { id: 'steps', label: 'Step Cards' },
  { id: 'checklist', label: 'Checklist Categories' },
  { id: 'step1', label: 'Step 1 Modal' },
  { id: 'queries', label: 'Queries Modal' },
  { id: 'step3', label: 'Step 3 Modal' },
  { id: 'messages', label: 'Toasts & Labels' },
];

const inputClass = 'w-full text-sm rounded-lg border border-gray-200 dark:border-dark-600 bg-white dark:bg-dark-800 px-3 py-2';
const labelClass = 'block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1';

const Field = ({ label, value, onChange, multiline = false }) => (
  <div>
    <label className={labelClass}>{label}</label>
    {multiline ? (
      <textarea className={inputClass} rows={2} value={value || ''} onChange={(e) => onChange(e.target.value)} />
    ) : (
      <input className={inputClass} value={value || ''} onChange={(e) => onChange(e.target.value)} />
    )}
  </div>
);

const BookkeepingPortalCMS = () => {
  const navigate = useNavigate();
  const { success: toastSuccess, error: toastError } = useToast();
  const [activeTab, setActiveTab] = useState('page');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [form, setForm] = useState(defaultForm());

  const fetchContent = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/portal-cms/admin/${SLUG}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data.success) {
        const c = res.data.data.content || {};
        setForm({
          ...defaultForm(),
          ...c,
          isPublished: res.data.data.isPublished !== false,
          stepCards: defaultForm().stepCards.map((card, index) => ({ ...card, ...(c.stepCards?.[index] || {}) })),
          checklistCategories: (c.checklistCategories?.length ? c.checklistCategories : defaultForm().checklistCategories),
          step1Modal: { ...c.step1Modal },
          queriesModal: { ...c.queriesModal, columns: { ...c.queriesModal?.columns } },
          step3Modal: { ...c.step3Modal },
          uploadModal: { ...c.uploadModal },
          toasts: { ...c.toasts },
        });
      }
    } catch {
      toastError('Failed to load bookkeeping page content');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContent();
  }, []);

  const updateField = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));
  const updateStepCard = (index, field, value) =>
    setForm((prev) => {
      const stepCards = [...prev.stepCards];
      stepCards[index] = { ...stepCards[index], [field]: value };
      return { ...prev, stepCards };
    });
  const updateModal = (section, field, value) =>
    setForm((prev) => ({ ...prev, [section]: { ...prev[section], [field]: value } }));
  const updateQueryColumn = (field, value) =>
    setForm((prev) => ({
      ...prev,
      queriesModal: { ...prev.queriesModal, columns: { ...prev.queriesModal.columns, [field]: value } },
    }));
  const updateToast = (field, value) =>
    setForm((prev) => ({ ...prev, toasts: { ...prev.toasts, [field]: value } }));

  const updateCategory = (index, field, value) =>
    setForm((prev) => {
      const checklistCategories = [...prev.checklistCategories];
      checklistCategories[index] = { ...checklistCategories[index], [field]: value };
      return { ...prev, checklistCategories };
    });

  const addCategory = () =>
    setForm((prev) => ({ ...prev, checklistCategories: [...prev.checklistCategories, emptyCategory()] }));

  const removeCategory = (index) =>
    setForm((prev) => ({
      ...prev,
      checklistCategories: prev.checklistCategories.filter((_, i) => i !== index),
    }));

  const moveCategory = (index, dir) =>
    setForm((prev) => {
      const checklistCategories = [...prev.checklistCategories];
      const next = index + dir;
      if (next < 0 || next >= checklistCategories.length) return prev;
      [checklistCategories[index], checklistCategories[next]] = [checklistCategories[next], checklistCategories[index]];
      return { ...prev, checklistCategories };
    });

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/api/portal-cms/admin/${SLUG}`,
        { content: form, isPublished: form.isPublished },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.success) toastSuccess('Bookkeeping page saved');
    } catch (err) {
      toastError(err.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Restore preloaded bookkeeping defaults? This cannot be undone.')) return;
    setResetting(true);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/portal-cms/admin/${SLUG}/reset`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.success) {
        toastSuccess('Defaults restored');
        await fetchContent();
      }
    } catch (err) {
      toastError(err.response?.data?.message || 'Failed to reset');
    } finally {
      setResetting(false);
    }
  };

  if (loading) {
    return (
      <PageLayout>
        <SkeletonLoader />
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <PageHeader
        title="Bookkeeping Service Page"
        subtitle="Edit user portal bookkeeping steps, checklist categories, modals, and messages"
        icon={FaBookOpen}
        onBack={() => navigate('/portal-cms')}
      />

      <div className="flex flex-wrap gap-2 mb-6">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveTab(tab.id)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              activeTab === tab.id
                ? 'bg-primary-600 text-white'
                : 'bg-gray-100 dark:bg-dark-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-dark-600'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="bg-white dark:bg-dark-800 rounded-2xl border border-gray-100 dark:border-dark-700 p-5 space-y-4 mb-6">
        {activeTab === 'page' && (
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Page title" value={form.pageTitle} onChange={(v) => updateField('pageTitle', v)} />
            <Field label="Loading message" value={form.loadingMessage} onChange={(v) => updateField('loadingMessage', v)} />
            <div className="md:col-span-2">
              <Field label="Page subtitle" value={form.pageSubtitle} onChange={(v) => updateField('pageSubtitle', v)} multiline />
            </div>
            <Field label="Bundle banner text" value={form.bundleBannerText} onChange={(v) => updateField('bundleBannerText', v)} />
            <Field label="Schedule banner title" value={form.scheduleBannerTitle} onChange={(v) => updateField('scheduleBannerTitle', v)} />
            <Field label="Schedule frequency prefix" value={form.scheduleFrequencyPrefix} onChange={(v) => updateField('scheduleFrequencyPrefix', v)} />
            <Field label="Schedule started prefix" value={form.scheduleStartedPrefix} onChange={(v) => updateField('scheduleStartedPrefix', v)} />
            <Field label="Due date label" value={form.scheduleDueDateLabel} onChange={(v) => updateField('scheduleDueDateLabel', v)} />
            <Field label="Financial records title" value={form.financialRecordsTitle} onChange={(v) => updateField('financialRecordsTitle', v)} />
            <Field label="Financial records empty text" value={form.financialRecordsEmpty} onChange={(v) => updateField('financialRecordsEmpty', v)} />
            <Field label="Error retry button" value={form.errorRetryLabel} onChange={(v) => updateField('errorRetryLabel', v)} />
            <Field label="Error refreshing label" value={form.errorRefreshingLabel} onChange={(v) => updateField('errorRefreshingLabel', v)} />
          </div>
        )}

        {activeTab === 'steps' && (
          <div className="space-y-6">
            {form.stepCards.map((card, index) => (
              <div key={card.step} className="p-4 rounded-xl border border-gray-100 dark:border-dark-700 space-y-3">
                <h4 className="font-semibold text-sm">Step {card.step}</h4>
                <Field label="Title" value={card.title} onChange={(v) => updateStepCard(index, 'title', v)} />
                <Field label="Instructions" value={card.instructions} onChange={(v) => updateStepCard(index, 'instructions', v)} multiline />
                <Field label="Status button label" value={card.statusMessage} onChange={(v) => updateStepCard(index, 'statusMessage', v)} />
              </div>
            ))}
          </div>
        )}

        {activeTab === 'checklist' && (
          <div className="space-y-4">
            <p className="text-xs text-amber-600 dark:text-amber-400">
              Category key is used in the database for uploaded files. Do not change keys after users have uploaded documents.
            </p>
            {form.checklistCategories.map((cat, index) => (
              <div key={index} className="p-4 rounded-xl border border-gray-100 dark:border-dark-700 space-y-3">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-semibold">Category {index + 1}</span>
                  <div className="flex gap-1">
                    <button type="button" onClick={() => moveCategory(index, -1)} className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-dark-700"><FaArrowUp className="w-3 h-3" /></button>
                    <button type="button" onClick={() => moveCategory(index, 1)} className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-dark-700"><FaArrowDown className="w-3 h-3" /></button>
                    <button type="button" onClick={() => removeCategory(index)} className="p-1.5 rounded text-red-500 hover:bg-red-50"><FaTrash className="w-3 h-3" /></button>
                  </div>
                </div>
                <Field label="Key (internal)" value={cat.key} onChange={(v) => updateCategory(index, 'key', v)} />
                <Field label="Display title" value={cat.title} onChange={(v) => updateCategory(index, 'title', v)} />
                <Field label="Description" value={cat.description} onChange={(v) => updateCategory(index, 'description', v)} multiline />
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={cat.isActive !== false} onChange={(e) => updateCategory(index, 'isActive', e.target.checked)} />
                  Active
                </label>
              </div>
            ))}
            <button type="button" onClick={addCategory} className="flex items-center gap-2 text-sm text-primary-600 font-medium">
              <FaPlus className="w-3 h-3" /> Add category
            </button>
          </div>
        )}

        {activeTab === 'step1' && (
          <div className="grid md:grid-cols-2 gap-4">
            {['title', 'subtitle', 'lockBanner', 'providedBadge', 'requiredBadge', 'addDocumentLabel', 'readyToUploadLabel', 'supportedFormats', 'closeLabel', 'saveDraftLabel', 'submitLabel'].map((key) => (
              <Field key={key} label={key} value={form.step1Modal[key]} onChange={(v) => updateModal('step1Modal', key, v)} multiline={key.includes('Banner') || key === 'subtitle'} />
            ))}
          </div>
        )}

        {activeTab === 'queries' && (
          <div className="grid md:grid-cols-2 gap-4">
            {['title', 'subtitle', 'lockBanner', 'emptyTitle', 'emptyText', 'autoSaveNote', 'autoSaveSaved', 'autoSaveError', 'closeLabel', 'saveDraftLabel', 'submitLabel', 'clientCommentPlaceholder', 'clientCommentSubmittedPlaceholder', 'clientReplyPlaceholder', 'clientReplyLockedPlaceholder', 'statusComplete', 'statusActionRequired', 'entityLabel'].map((key) => (
              <Field key={key} label={key} value={form.queriesModal[key]} onChange={(v) => updateModal('queriesModal', key, v)} multiline={key.includes('Banner') || key === 'subtitle' || key === 'emptyText'} />
            ))}
            <div className="md:col-span-2 border-t border-gray-100 dark:border-dark-700 pt-4">
              <h4 className="font-semibold text-sm mb-3">Table column headers</h4>
              <div className="grid md:grid-cols-2 gap-4">
                {['srNo', 'date', 'accountName', 'description', 'amount', 'bookscomplyComment', 'clientComment', 'status'].map((key) => (
                  <Field key={key} label={key} value={form.queriesModal.columns?.[key]} onChange={(v) => updateQueryColumn(key, v)} />
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'step3' && (
          <div className="grid md:grid-cols-2 gap-4">
            {['title', 'subtitle', 'emptyTitle', 'emptyText', 'documentsLabel', 'viewDownloadLabel', 'questionsLabel', 'reviewRequestLabel', 'defaultQuestion', 'decisionLabel', 'approveLabel', 'rejectLabel', 'commentLabel', 'commentPlaceholder', 'historyTitle', 'approveSubmitLabel', 'submitFeedbackLabel', 'commentRequiredToast', 'approveSuccessToast', 'rejectSuccessToast'].map((key) => (
              <Field key={key} label={key} value={form.step3Modal[key]} onChange={(v) => updateModal('step3Modal', key, v)} multiline={key.includes('Text') || key.includes('Placeholder') || key === 'subtitle'} />
            ))}
          </div>
        )}

        {activeTab === 'messages' && (
          <div className="grid md:grid-cols-2 gap-4">
            {['queriesUnderReview', 'stepLocked', 'finalBooksReady', 'step4Locked', 'queriesLocked', 'commentsSubmitted', 'commentsDraftSaved', 'checklistSubmitted', 'checklistDraftSaved', 'fileRemoved', 'filesUploaded', 'selectFileRequired', 'maxFileSize', 'userMissing'].map((key) => (
              <Field key={key} label={key} value={form.toasts[key]} onChange={(v) => updateToast(key, v)} multiline />
            ))}
            <div className="md:col-span-2 border-t border-gray-100 dark:border-dark-700 pt-4">
              <h4 className="font-semibold text-sm mb-3">Upload modal (Step 3)</h4>
              <div className="grid md:grid-cols-2 gap-4">
                {['step3TitlePrefix', 'step3Description', 'dropHint', 'selectedFilesLabel', 'uploadLabel', 'uploadingLabel'].map((key) => (
                  <Field key={key} label={key} value={form.uploadModal[key]} onChange={(v) => updateModal('uploadModal', key, v)} />
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <button type="button" onClick={handleSave} disabled={saving} className="btn-premium flex items-center gap-2">
          <FaSave /> {saving ? 'Saving...' : 'Save Changes'}
        </button>
        <button type="button" onClick={handleReset} disabled={resetting} className="px-4 py-2 rounded-xl border border-gray-200 dark:border-dark-600 text-sm font-medium flex items-center gap-2">
          <FaUndo /> {resetting ? 'Resetting...' : 'Reset to Defaults'}
        </button>
        <label className="flex items-center gap-2 text-sm ml-auto">
          <input type="checkbox" checked={form.isPublished !== false} onChange={(e) => updateField('isPublished', e.target.checked)} />
          Published
        </label>
      </div>
    </PageLayout>
  );
};

export default BookkeepingPortalCMS;
